import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { ArrowLeft, Info } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

export const Route = createFileRoute("/relaxation-practices")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Relaxation Practices — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gentle ways to slow down, unwind, and create moments of calm throughout your day.",
      },
      {
        property: "og:title",
        content: "Relaxation Practices — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Swipeable, trauma-informed cards for breathing, calming routines, evening wind-down, self-soothing and rest.",
      },
      {
        property: "og:url",
        content: "https://recoverytrauma.com/relaxation-practices",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://recoverytrauma.com/relaxation-practices",
      },
    ],
  }),
  component: withMemberGate(RelaxationPracticesPage, "Relaxation Practices"),
});

type RelaxCard = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  ideas: string[];
  tint: string;
};

const CARDS: RelaxCard[] = [
  {
    id: "gentle",
    emoji: "🌿",
    title: "Gentle Relaxation",
    description: "Simple practices to help the body and mind soften.",
    ideas: [
      "Box Breathing",
      "Extended Exhale Breathing",
      "Body Scan",
      "Progressive Muscle Relaxation",
    ],
    tint: "#7BAE7F",
  },
  {
    id: "routines",
    emoji: "☕",
    title: "Creating Calming Routines",
    description:
      "Small rituals can help create predictability and safety.",
    ideas: [
      "Morning grounding routine",
      "Evening wind-down routine",
      "Tea and reflection practice",
      "Technology-free moments",
    ],
    tint: "#C9A66B",
  },
  {
    id: "evening",
    emoji: "🌙",
    title: "Evening Wind-Down",
    description: "Support restful sleep and nervous system recovery.",
    ideas: [
      "Dim the lights",
      "Gentle stretching",
      "Relaxation sounds",
      "Bedtime breathing",
    ],
    tint: "#A8C0D9",
  },
  {
    id: "soothing",
    emoji: "💛",
    title: "Self-Soothing",
    description: "Comforting activities for difficult moments.",
    ideas: [
      "Wrap up in a blanket",
      "Hold a warm drink",
      "Listen to calming music",
      "Use a comfort object",
    ],
    tint: "#E8C07A",
  },
  {
    id: "sensory",
    emoji: "🎵",
    title: "Sensory Wellbeing",
    description: "Explore calming sensory experiences.",
    ideas: [
      "Soft lighting",
      "Nature sounds",
      "Aromatherapy",
      "Comfortable textures",
    ],
    tint: "#D58FB4",
  },
  {
    id: "pause",
    emoji: "🕯️",
    title: "Rest & Pause",
    description: "Permission to slow down.",
    ideas: [
      "Sit quietly for 5 minutes",
      "Notice your breathing",
      "Watch a candle flame",
      "Take a mindful break",
    ],
    tint: "#E8A87C",
  },
];

const REFLECTION_PROMPTS = [
  "What helped you feel calmer?",
  "What did you notice in your body?",
  "Is there anything you would like to do again?",
];

function RelaxationPracticesPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      {/* Back link */}
      <div className="mb-6">
        <Link
          to="/holistic-wellbeing"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Wellbeing Hub
        </Link>
      </div>

      {/* Hero */}
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <span aria-hidden>🕯️</span> Relaxation Practices
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Gentle ways to slow down and unwind
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Create moments of calm throughout your day — swipe through the
          cards and choose what feels right for now.
        </p>
      </header>

      {/* Non-clinical notice */}
      <div className="mb-8 flex items-start gap-3 rounded-2xl border border-gold/30 bg-cream/40 p-4 text-sm text-foreground/85">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
        <p>
          These are supportive, non-clinical practices — not therapy. If
          something becomes overwhelming, pause and reach out to{" "}
          <Link to="/crisis" className="font-semibold underline">
            crisis resources
          </Link>
          .
        </p>
      </div>

      {/* Carousel */}
      <Carousel
        opts={{ align: "start", loop: true }}
        className="w-full"
        aria-label="Relaxation practices"
      >
        <CarouselSwipeHint
          label="Swipe to explore"
          storageKey="relaxation-practices"
        />
        <CarouselContent className="-ms-5">
          {CARDS.map((c) => (
            <CarouselItem
              key={c.id}
              className="basis-[88%] ps-5 sm:basis-1/2 lg:basis-1/2"
            >
              <article
                className={[
                  "group relative flex h-full flex-col rounded-[2rem] p-6 transition-all duration-300 sm:p-8",
                  "hover:-translate-y-1",
                  "bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)]",
                  "dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)]",
                  "border border-gold-light/60 dark:border-gold-light/40",
                  "shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]",
                ].join(" ")}
              >
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  <span aria-hidden>🕯️</span> Relaxation practice
                </div>

                <div className="mt-3 flex items-start gap-4">
                  <div
                    aria-hidden
                    className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl text-5xl ring-1 ring-gold-light/50 sm:h-28 sm:w-28 sm:text-6xl"
                    style={{
                      background: `radial-gradient(ellipse at center, color-mix(in oklab, ${c.tint} 30%, var(--cream)) 0%, color-mix(in oklab, ${c.tint} 12%, var(--cream)) 75%, transparent 100%)`,
                    }}
                  >
                    <span>{c.emoji}</span>
                  </div>
                  <div className="min-w-0 pt-1">
                    <h2 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                      {c.title}
                    </h2>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-foreground/80 sm:text-base">
                  {c.description}
                </p>

                <div className="mt-5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                    Ideas
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                    {c.ideas.map((idea) => (
                      <li key={idea} className="flex items-start gap-2">
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]"
                        />
                        <span>{idea}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-6">
                  <div className="rounded-2xl border border-gold/30 bg-cream/50 p-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                      <span aria-hidden>💛</span> Gentle reflection
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-foreground/85 sm:text-sm">
                      {REFLECTION_PROMPTS.map((p) => (
                        <li key={p} className="flex items-start gap-2">
                          <span aria-hidden className="text-[var(--gold-deep)]">
                            •
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/journal"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline"
                    >
                      Capture in Journal →
                    </Link>
                  </div>
                </div>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="-start-3 hidden sm:flex" />
        <CarouselNext className="-end-3 hidden sm:flex" />
      </Carousel>
    </div>
  );
}
